import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Stack, Tooltip, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import makeStyles from "@mui/styles/makeStyles";
import ConfirmationModal from "common/components/ConfirmationModal";
import EntaLink from "common/components/EntaLink";
import useWorkspaces from "common/hooks/useWorkspaces";
import routes from "developer/routes";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getFormDefinitionStages } from "../store/formDefinitionStagesSlice";
import {
  FormDefinition,
  deleteFormDefinition,
  getFormDefinitions,
} from "../store/formDefinitionsSlice";
import EditFormDrawer from "./EditFormDrawer/EditFormDrawer";
import NoRows from "./no-rows";

type CRUDTableProps = {
  workspaceIdSelected: string | null;
};

const useStyles = makeStyles({
  cell: {
    maxWidth: 400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

const CRUDTable: React.FC<CRUDTableProps> = ({ workspaceIdSelected }) => {
  const dispatch = useAppDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const [formDefinition, setFormDefinition] = useState<FormDefinition>({
    id: "",
    name: "",
    description: "",
    workspaceId: "",
    disabled: false,
  });
  const classes = useStyles();

  const { list } = useAppSelector((state) => {
    return state.developer.formsCRUD;
  });

  const { stages } = useAppSelector((state) => {
    return state.developer.stagesCRUD;
  });

  const hashWorkspaces = useWorkspaces();

  const workspaces = useMemo(() => {
    return Object.values(hashWorkspaces);
  }, [hashWorkspaces]);

  const listFormDefinitions = useMemo(() => {
    if (workspaceIdSelected) {
      return list.filter((item) => item.workspaceId === workspaceIdSelected);
    } else {
      return list;
    }
  }, [list, workspaceIdSelected]);

  useEffect(() => {
    dispatch(getFormDefinitions());
  }, [dispatch]);

  useEffect(() => {
    if (isEditModalOpen && !stages) {
      dispatch(getFormDefinitionStages(formDefinition.id));
    }
  }, [dispatch, formDefinition.id, isEditModalOpen, stages]);

  const onDeleteForm = (formDefinition: FormDefinition) => {
    setIsDeleteModalOpen(true);
    setFormDefinition(formDefinition);
  };

  const onEditForm = (formDefinition: FormDefinition) => {
    setIsEditModalOpen(true);
    setFormDefinition(formDefinition);
  };

  const cancelDeleteForm = () => {
    setIsDeleteModalOpen(false);
  };

  const confirmDeleteForm = () => {
    setIsDeleteModalOpen(false);
    dispatch(deleteFormDefinition(formDefinition.id));
  };

  return (
    <>
      <EditFormDrawer
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        formDefinitionList={list}
        formDefinition={formDefinition}
        workspaces={workspaces}
      />
      <ConfirmationModal
        open={isDeleteModalOpen}
        onConfirm={confirmDeleteForm}
        onCancel={cancelDeleteForm}
        title="Confirm deletion?"
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        mainButtonType="error"
        defaultAction="cancel"
      >
        You are about to delete {formDefinition.name} form definition.
      </ConfirmationModal>

      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Workspace</TableCell>
            <TableCell>Disabled</TableCell>
            <TableCell width="auto" align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {listFormDefinitions.length === 0 && <NoRows />}
          {listFormDefinitions.map((formDefinition) => (
            <TableRow key={formDefinition.id}>
              <TableCell component="td" scope="row" className={classes.cell}>
                <EntaLink
                  to={`${routes.FORM_DEFINITIONS}/${formDefinition.id}`}
                >
                  <Tooltip title={formDefinition.name} placement="bottom-start">
                    <Typography variant="body2">
                      {formDefinition.name}
                    </Typography>
                  </Tooltip>
                </EntaLink>
              </TableCell>
              <TableCell component="td" scope="row" className={classes.cell}>
                {formDefinition.description && (
                  <Tooltip
                    title={formDefinition.description}
                    placement="left-start"
                  >
                    <Typography variant="body2">
                      {formDefinition.description}
                    </Typography>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell component="td" scope="row">
                {hashWorkspaces[formDefinition.workspaceId]?.name}
              </TableCell>
              <TableCell>{formDefinition.disabled && <CheckIcon />}</TableCell>
              <TableCell align="right">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <IconButton
                    aria-label="edit"
                    onClick={() => onEditForm(formDefinition)}
                    size="large"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    onClick={() => onDeleteForm(formDefinition)}
                    size="large"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default CRUDTable;
